import { createDraftSafeSelector as createSelector } from '@reduxjs/toolkit';

import { Inject } from '@vk-hr-tek/core/ioc';
import { FormatService } from '@vk-hr-tek/core/format';

import { EventListItem as EventListEntity } from '@app/gen/events';

import { ReviewListMapper, ReviewMapper } from '../mappers';
import { REVIEW_TAG } from '../constants';

import { ReviewWithRootState as RootState } from './review.state';

export const selectEvents = createSelector(
  [
    (state: RootState) => state.review.currentIds,
    (state: RootState) => state.review.entities,
    (_: RootState, inject: Inject) => inject(ReviewListMapper),
  ],
  (currentIds, entities, format) => {
    return currentIds
      .map((id) => entities[id])
      .filter((x): x is EventListEntity => x !== undefined)
      .map((event) => format.processListEntity({ ...event }));
  },
);

export const selectFullEvents = createSelector(
  [(state: RootState) => state.review],
  (eventsState) => {
    const { entities, currentIds, total } = eventsState;
    const events = currentIds
      .map((id) => entities[id])
      .filter((x): x is EventListEntity => x !== undefined);

    return { events, total };
  },
);

export const selectTotal = (state: RootState) => state.review.total;

export const selectTotalMy = (state: RootState, inject?: Inject) => {
  const format = (inject as unknown as Inject)(FormatService);

  return state.review.totalMy ? format.convertNumber(state.review.totalMy) : '';
};

export const selectTotalMyTeam = createSelector(
  [
    (state: RootState) => state.review.totalMyTeam,
    (_: RootState, inject: Inject) => inject(FormatService),
  ],
  (totalMyTeam, format) => totalMyTeam && format.convertNumber(totalMyTeam),
);

export const selectCompanyFilters = createSelector(
  [
    (state: RootState) => state.review.filters,
    (_: RootState, inject: Inject) => inject(ReviewMapper),
  ],
  (filters, format) =>
    filters.data &&
    format.processCompanyListFilters(filters.data, [REVIEW_TAG]),
);

export const selectFinished = (state: RootState) => state.review.finished;

export const selectEventTypeOptions = (state: RootState) =>
  state.review.eventTypes.companies;

export const selectEmployeeFilters = createSelector(
  [
    (state: RootState) => state.review.filters,
    (_: RootState, inject: Inject) => inject(ReviewMapper),
  ],
  (filters, format) =>
    filters.data && format.processListFilters(filters.data, [REVIEW_TAG]),
);

export const selectError = (state: RootState) => state.review.error;

export const selectStatus = (state: RootState) => state.review.status;
